<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <v-row justify="center" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-3 elevation-2">
          <v-icon color="white">mdi-check-decagram</v-icon>
        </v-avatar>
        <div>
          <h1 class="text-h5 font-weight-bold grey--text text--darken-3 mb-0">
            Aprobaciones Comerciales
          </h1>
          <span class="text-caption grey--text">DGII - Gestión de aprobaciones comerciales electrónicas</span>
        </div>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-row justify="center">
      <search-header
        class="pt-0 mt-0 pb-2"
        @dataFilters="changeBusiness($event)"
        :selected_week="4"
        :show_department="false"
        :show_location="false"
        :type="false"
      />
    </v-row>

    <!-- KPI Cards -->
    <v-fade-transition>
      <v-row v-if="!loading && hasData" class="mb-4" dense>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg overflow-hidden" elevation="2" @click="activeTab = 0" style="cursor: pointer">
            <div class="d-flex align-center pa-4">
              <v-avatar color="amber lighten-4" size="48" class="mr-3">
                <v-icon color="amber darken-3">mdi-clock-alert-outline</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text text-uppercase font-weight-bold">Pendientes</div>
                <div class="text-h5 font-weight-black amber--text text--darken-3">{{ pendingInvoices.length }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg overflow-hidden" elevation="2" @click="activeTab = 1" style="cursor: pointer">
            <div class="d-flex align-center pa-4">
              <v-avatar color="blue lighten-4" size="48" class="mr-3">
                <v-icon color="blue darken-1">mdi-send-check</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text text-uppercase font-weight-bold">Enviadas</div>
                <div class="text-h5 font-weight-black">{{ approvalsSent.length }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg overflow-hidden" elevation="2" @click="activeTab = 2" style="cursor: pointer">
            <div class="d-flex align-center pa-4">
              <v-avatar color="green lighten-4" size="48" class="mr-3">
                <v-icon color="green darken-1">mdi-inbox-arrow-down</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text text-uppercase font-weight-bold">Recibidas</div>
                <div class="text-h5 font-weight-black">{{ approvalsReceived.length }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg overflow-hidden" elevation="2">
            <div class="d-flex align-center pa-4">
              <v-avatar :color="errorCount > 0 ? 'red lighten-4' : 'green lighten-4'" size="48" class="mr-3">
                <v-icon :color="errorCount > 0 ? 'red darken-1' : 'green darken-1'">
                  {{ errorCount > 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                </v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text text-uppercase font-weight-bold">Con Errores</div>
                <div class="text-h5 font-weight-black">{{ errorCount }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>

    <!-- Tabs -->
    <v-card class="rounded-lg elevation-2">
      <v-tabs v-model="activeTab" background-color="primary" dark slider-color="white" slider-size="3">
        <v-tab>
          <v-icon left small>mdi-clock-alert-outline</v-icon>
          Pendientes
          <v-chip v-if="pendingInvoices.length" x-small class="ml-2" color="amber" text-color="white">
            {{ pendingInvoices.length }}
          </v-chip>
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-send-check</v-icon>
          Enviadas
          <v-chip v-if="approvalsSent.length" x-small class="ml-2" color="white" text-color="primary">
            {{ approvalsSent.length }}
          </v-chip>
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-inbox-arrow-down</v-icon>
          Recibidas
          <v-chip v-if="approvalsReceived.length" x-small class="ml-2" color="white" text-color="primary">
            {{ approvalsReceived.length }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- ============ TAB 1: FACTURAS PENDIENTES DE APROBACIÓN ============ -->
        <v-tab-item>
          <v-card-text class="pa-0">
            <v-expand-transition>
              <v-row v-if="loading" justify="center" class="pa-6">
                <v-col cols="12" md="10">
                  <v-skeleton-loader type="table-heading, table-tbody"></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-expand-transition>
            <v-data-table
              v-if="!loading"
              v-model="selectedPending"
              :headers="headersPending"
              :items="pendingInvoices"
              :search="searchPending"
              :items-per-page="50"
              show-select
              dense
              fixed-header
              height="55vh"
              hide-default-footer
              item-key="Id"
              @page-count="pageCountPending = $event"
              :page.sync="pagePending"
              class="modern-table"
            >
              <template v-slot:top>
                <v-toolbar flat class="rounded-0">
                  <v-btn
                    color="success"
                    rounded small
                    class="mr-2"
                    :disabled="selectedPending.length === 0"
                    :loading="submitting"
                    @click="openBulkApproveDialog"
                  >
                    <v-icon left small>mdi-check-all</v-icon>
                    Aprobar ({{ selectedPending.length }})
                  </v-btn>
                  <v-btn
                    color="error"
                    rounded small outlined
                    :disabled="selectedPending.length === 0"
                    @click="openBulkReject"
                  >
                    <v-icon left small>mdi-close-circle-outline</v-icon>
                    Rechazar ({{ selectedPending.length }})
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="searchPending"
                      label="Buscar..."
                      outlined dense hide-details
                      prepend-inner-icon="mdi-magnify"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-toolbar>
              </template>
              <template v-slot:[`item.Encf`]="{ item }">
                <span class="font-weight-bold primary--text cursor-pointer hover-underline" @click="openCompareDialog(item)">{{ item.Encf }}</span>
              </template>
              <template v-slot:[`item.MontoTotal`]="{ item }">
                <span class="font-weight-bold">{{ formatCurrency(item.MontoTotal) }}</span>
              </template>
              <template v-slot:[`item.FechaEmision`]="{ item }">
                {{ formatDate(item.FechaEmision) }}
              </template>
              <template v-slot:[`item.FechaRecepcion`]="{ item }">
                {{ formatDate(item.FechaRecepcion) }}
              </template>
              <template v-slot:[`item.Estado`]="{ item }">
                <v-chip x-small label :color="getStatusColor(item.Estado)" :text-color="getStatusTextColor(item.Estado)" class="font-weight-bold">
                  {{ item.Estado }}
                </v-chip>
              </template>
              <template v-slot:[`item.existeEnSistema`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-if="item.existeEnSistema" small color="success" v-on="on">mdi-check-decagram</v-icon>
                    <v-icon v-else small color="grey lighten-1" v-on="on">mdi-close-circle-outline</v-icon>
                  </template>
                  <span>{{ item.existeEnSistema ? 'Registrada en el sistema' : 'No encontrada en el sistema' }}</span>
                </v-tooltip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex align-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small color="primary" class="mr-1" v-on="on" @click="openCompareDialog(item)">
                        <v-icon x-small>mdi-compare-horizontal</v-icon>
                      </v-btn>
                    </template>
                    <span>Comparar facturas</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small color="primary" v-on="on" @click="openApproveDialog(item)">
                        <v-icon x-small>mdi-file-check-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Gestionar aprobación</span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-10">
                  <v-icon size="64" color="green lighten-2">mdi-check-circle-outline</v-icon>
                  <div class="text-body-1 grey--text mt-3 font-weight-medium">No hay facturas pendientes de aprobación</div>
                  <div class="text-caption grey--text">Todas las facturas recibidas han sido procesadas</div>
                </div>
              </template>
              <template v-slot:footer>
                <v-row justify="center">
                  <v-container style="width: 70%" class="mt-4">
                    <v-pagination v-model="pagePending" :length="pageCountPending" color="primary"></v-pagination>
                  </v-container>
                </v-row>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>

        <!-- ============ TAB 2: APROBACIONES ENVIADAS ============ -->
        <v-tab-item>
          <v-card-text class="pa-0">
            <v-expand-transition>
              <v-row v-if="loading" justify="center" class="pa-6">
                <v-col cols="12" md="10">
                  <v-skeleton-loader type="table-heading, table-tbody"></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-expand-transition>
            <v-data-table
              v-if="!loading"
              :headers="headersSent"
              :items="approvalsSent"
              :search="searchSent"
              :items-per-page="50"
              dense
              fixed-header
              height="60vh"
              hide-default-footer
              @page-count="pageCountSent = $event"
              :page.sync="pageSent"
              class="modern-table"
            >
              <template v-slot:top>
                <v-toolbar flat class="rounded-0">
                  <v-spacer></v-spacer>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="searchSent"
                      label="Buscar..."
                      outlined dense hide-details
                      prepend-inner-icon="mdi-magnify"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-spacer></v-spacer>
                </v-toolbar>
              </template>
              <template v-slot:[`item.EncfRelacionado`]="{ item }">
                <span class="font-weight-bold primary--text">{{ item.EncfRelacionado }}</span>
              </template>
              <template v-slot:[`item.FechaProcesamiento`]="{ item }">
                {{ formatDate(item.FechaProcesamiento) }}
              </template>
              <template v-slot:[`item.FechaEnvio`]="{ item }">
                {{ formatDate(item.FechaEnvio) }}
              </template>
              <template v-slot:[`item.FechaCreacion`]="{ item }">
                {{ formatDate(item.FechaCreacion) }}
              </template>
              <template v-slot:[`item.Estado`]="{ item }">
                <v-chip x-small label :color="getStatusColor(item.Estado)" :text-color="getStatusTextColor(item.Estado)" class="font-weight-bold">
                  {{ item.Estado }}
                </v-chip>
              </template>
              <template v-slot:[`item.IntentosEnvio`]="{ item }">
                <v-chip x-small outlined :color="item.IntentosEnvio > 3 ? 'error' : 'grey'">
                  {{ item.IntentosEnvio }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon x-small color="primary" @click="showDetail(item, 'enviada')">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-10">
                  <v-icon size="64" color="grey lighten-2">mdi-send-check</v-icon>
                  <div class="text-body-2 grey--text mt-3">No hay aprobaciones enviadas para los filtros seleccionados</div>
                </div>
              </template>
              <template v-slot:footer>
                <v-row justify="center">
                  <v-container style="width: 70%" class="mt-4">
                    <v-pagination v-model="pageSent" :length="pageCountSent" color="primary"></v-pagination>
                  </v-container>
                </v-row>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>

        <!-- ============ TAB 3: APROBACIONES RECIBIDAS (RESPUESTAS DGII) ============ -->
        <v-tab-item>
          <v-card-text class="pa-0">
            <v-expand-transition>
              <v-row v-if="loading" justify="center" class="pa-6">
                <v-col cols="12" md="10">
                  <v-skeleton-loader type="table-heading, table-tbody"></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-expand-transition>
            <v-data-table
              v-if="!loading"
              :headers="headersReceived"
              :items="approvalsReceived"
              :search="searchReceived"
              :items-per-page="50"
              dense
              fixed-header
              height="60vh"
              hide-default-footer
              @page-count="pageCountReceived = $event"
              :page.sync="pageReceived"
              class="modern-table"
            >
              <template v-slot:top>
                <v-toolbar flat class="rounded-0">
                  <v-spacer></v-spacer>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="searchReceived"
                      label="Buscar..."
                      outlined dense hide-details
                      prepend-inner-icon="mdi-magnify"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-spacer></v-spacer>
                </v-toolbar>
              </template>
              <template v-slot:[`item.EncfRelacionado`]="{ item }">
                <span class="font-weight-bold primary--text">{{ item.EncfRelacionado }}</span>
              </template>
              <template v-slot:[`item.FechaRecepcion`]="{ item }">
                {{ formatDate(item.FechaRecepcion) }}
              </template>
              <template v-slot:[`item.FechaAprobacionComercial`]="{ item }">
                {{ formatDate(item.FechaAprobacionComercial) }}
              </template>
              <template v-slot:[`item.FechaCreacion`]="{ item }">
                {{ formatDate(item.FechaCreacion) }}
              </template>
              <template v-slot:[`item.MontoTotal`]="{ item }">
                <span class="font-weight-bold">{{ formatCurrency(item.MontoTotal) }}</span>
              </template>
              <template v-slot:[`item.MontoITBIS`]="{ item }">
                {{ formatCurrency(item.MontoITBIS) }}
              </template>
              <template v-slot:[`item.Estado`]="{ item }">
                <v-chip x-small label :color="getStatusColor(item.Estado)" :text-color="getStatusTextColor(item.Estado)" class="font-weight-bold">
                  {{ item.Estado || 'SIN ESTADO' }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon x-small color="primary" @click="showDetail(item, 'recibida')">
                  <v-icon x-small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-10">
                  <v-icon size="64" color="grey lighten-2">mdi-inbox-arrow-down</v-icon>
                  <div class="text-body-2 grey--text mt-3">No hay respuestas de DGII para los filtros seleccionados</div>
                </div>
              </template>
              <template v-slot:footer>
                <v-row justify="center">
                  <v-container style="width: 70%" class="mt-4">
                    <v-pagination v-model="pageReceived" :length="pageCountReceived" color="primary"></v-pagination>
                  </v-container>
                </v-row>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- ============ DIALOG DETALLE ============ -->
    <v-dialog v-model="dialogDetail" max-width="800" transition="dialog-bottom-transition">
      <v-card class="rounded-xl overflow-hidden" v-if="selectedItem">
        <v-toolbar flat :color="detailType === 'recibida' ? 'green darken-1' : 'blue darken-1'" dark>
          <v-icon left>
            {{ detailType === 'recibida' ? 'mdi-inbox-arrow-down' : 'mdi-send-check' }}
          </v-icon>
          <v-toolbar-title class="font-weight-bold">
            Detalle - {{ detailType === 'recibida' ? 'Respuesta DGII' : 'Aprobación Enviada' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogDetail = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-row dense>
            <v-col cols="12" md="6" v-for="(field, idx) in detailFields" :key="idx">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">{{ field.label }}</div>
                <div class="text-body-2 font-weight-medium grey--text text--darken-3">
                  <v-chip v-if="field.isStatus" x-small label :color="getStatusColor(field.value)" :text-color="getStatusTextColor(field.value)" class="font-weight-bold">
                    {{ field.value || '-' }}
                  </v-chip>
                  <span v-else-if="field.isCurrency" class="font-weight-bold primary--text">{{ formatCurrency(field.value) }}</span>
                  <span v-else-if="field.isDate">{{ formatDate(field.value) }}</span>
                  <span v-else>{{ field.value || '-' }}</span>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" v-if="selectedItem.Mensaje || selectedItem.DetalleError || selectedItem.DetalleMotivoRechazo || selectedItem.MotivoRechazo"></v-divider>

          <v-alert v-if="selectedItem.Mensaje" type="info" text dense class="mb-3">
            <div class="text-overline mb-1">Mensaje</div>
            <div class="text-body-2">{{ selectedItem.Mensaje }}</div>
          </v-alert>
          <v-alert v-if="selectedItem.DetalleError" type="error" text dense class="mb-3">
            <div class="text-overline mb-1">Detalle de Error</div>
            <div class="text-body-2">{{ selectedItem.DetalleError }}</div>
          </v-alert>
          <v-alert v-if="selectedItem.DetalleMotivoRechazo" type="warning" text dense class="mb-3">
            <div class="text-overline mb-1">Motivo de Rechazo ({{ selectedItem.CodigoMotivoRechazo || '-' }})</div>
            <div class="text-body-2">{{ selectedItem.DetalleMotivoRechazo }}</div>
          </v-alert>
          <v-alert v-if="selectedItem.MotivoRechazo" type="warning" text dense>
            <div class="text-overline mb-1">Motivo de Rechazo ({{ selectedItem.CodigoMotivoRechazo || '-' }})</div>
            <div class="text-body-2">{{ selectedItem.MotivoRechazo }}</div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ============ DIALOG APROBACIÓN ============ -->
    <v-dialog v-model="dialogApprove" max-width="700" transition="dialog-bottom-transition">
      <v-card class="rounded-xl overflow-hidden" v-if="approveItem">
        <v-toolbar flat color="success darken-1" dark>
          <v-icon left>mdi-file-check-outline</v-icon>
          <v-toolbar-title class="font-weight-bold">
            {{ approveBulkItems.length > 1 ? `Aprobar ${approveBulkItems.length} facturas` : 'Gestionar Aprobación' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogApprove = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Single item detail -->
        <v-card-text class="pa-6" v-if="approveBulkItems.length <= 1">
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">e-NCF</div>
                <div class="text-body-1 font-weight-black primary--text">{{ approveItem.Encf }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">Emisor</div>
                <div class="text-body-2 font-weight-medium">{{ approveItem.emisorName || approveItem.RncEmisor }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">RNC Emisor</div>
                <div class="text-body-2 font-weight-medium">{{ approveItem.RncEmisor }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">Monto Total</div>
                <div class="text-body-1 font-weight-bold primary--text">{{ formatCurrency(approveItem.MontoTotal) }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">Fecha Emisión</div>
                <div class="text-body-2 font-weight-medium">{{ formatDate(approveItem.FechaEmision) }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">Fecha Recepción</div>
                <div class="text-body-2 font-weight-medium">{{ formatDate(approveItem.FechaRecepcion) }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">Estado</div>
                <v-chip x-small label :color="getStatusColor(approveItem.Estado)" :text-color="getStatusTextColor(approveItem.Estado)" class="font-weight-bold">
                  {{ approveItem.Estado }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-overline grey--text mb-0" style="line-height: 1.2">En Sistema</div>
                <v-chip x-small label :color="approveItem.existeEnSistema ? 'success lighten-5' : 'grey lighten-3'" :text-color="approveItem.existeEnSistema ? 'success' : 'grey'" class="font-weight-bold">
                  {{ approveItem.existeEnSistema ? 'Registrada' : 'No encontrada' }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Bulk items summary -->
        <v-card-text class="pa-6" v-else>
          <v-alert type="info" text dense class="mb-4">
            Se procesarán <strong>{{ approveBulkItems.length }}</strong> facturas seleccionadas por un total de <strong>{{ formatCurrency(approveBulkTotal) }}</strong>
          </v-alert>
          <v-simple-table dense style="max-height: 300px; overflow-y: auto">
            <thead>
              <tr>
                <th>e-NCF</th>
                <th>Emisor</th>
                <th class="text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inv in approveBulkItems" :key="inv.Id">
                <td class="text-caption font-weight-bold primary--text">{{ inv.Encf }}</td>
                <td class="text-caption">{{ inv.emisorName || inv.RncEmisor }}</td>
                <td class="text-caption text-right font-weight-bold">{{ formatCurrency(inv.MontoTotal) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-6 py-4">
          <v-btn
            color="error"
            outlined rounded
            :disabled="submitting"
            @click="dialogApprove = false; openRejectFromApproveDialog()"
          >
            <v-icon left small>mdi-close-circle</v-icon>
            Rechazar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogApprove = false" :disabled="submitting">Cancelar</v-btn>
          <v-btn
            color="success"
            rounded
            :loading="submitting"
            @click="confirmApproveFromDialog"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            {{ approveBulkItems.length > 1 ? `Aprobar (${approveBulkItems.length})` : 'Aprobar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ DIALOG RECHAZO ============ -->
    <v-dialog v-model="dialogReject" max-width="550" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <v-toolbar flat color="error" dark>
          <v-icon left>mdi-close-circle</v-icon>
          <v-toolbar-title class="font-weight-bold">Rechazar Factura</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogReject = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-alert type="warning" text dense class="mb-4">
            <span v-if="rejectItems.length === 1">
              Se rechazará la factura <strong>{{ rejectItems[0].Encf }}</strong> del emisor <strong>{{ rejectItems[0].RncEmisor }}</strong>
            </span>
            <span v-else>
              Se rechazarán <strong>{{ rejectItems.length }}</strong> facturas seleccionadas
            </span>
          </v-alert>

          <v-select
            v-model="rejectForm.codigoMotivo"
            :items="rejectionReasons"
            item-text="text"
            item-value="value"
            label="Motivo de rechazo"
            outlined dense
            :rules="[v => !!v || 'Seleccione un motivo']"
          >
            <template v-slot:prepend-inner>
              <v-icon color="error" size="20">mdi-alert-outline</v-icon>
            </template>
          </v-select>

          <v-textarea
            v-model="rejectForm.detalle"
            label="Detalle del rechazo (opcional)"
            outlined dense
            rows="3"
            counter="500"
            maxlength="500"
          ></v-textarea>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogReject = false" :disabled="submitting">Cancelar</v-btn>
          <v-btn
            color="error"
            rounded
            :loading="submitting"
            :disabled="!rejectForm.codigoMotivo"
            @click="confirmReject"
          >
            <v-icon left small>mdi-close-circle</v-icon>
            Confirmar Rechazo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ DIALOG COMPARACIÓN FULLSCREEN ============ -->
    <v-dialog v-model="dialogCompare" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card class="compare-dialog-card" tile>
        <v-toolbar dark color="primary" dense flat>
          <v-btn icon dark @click="dialogCompare = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title class="font-weight-bold">
            Comparación de Factura — {{ compareEncf }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip v-if="compareSystemInvoice && compareDgiiInvoice" dark small color="green" class="mr-2">
            <v-icon left x-small>mdi-check-circle</v-icon> Ambas encontradas
          </v-chip>
          <v-chip v-else-if="!compareLoading" dark small color="warning" class="mr-2">
            <v-icon left x-small>mdi-alert</v-icon> Datos incompletos
          </v-chip>
          <template v-if="compareOriginalItem && compareOriginalItem.Encf && !compareLoading">
            <v-btn dark small rounded color="green accent-3" class="mr-2 black--text" :loading="submitting" @click="approveFromCompare">
              <v-icon left small>mdi-check-circle</v-icon> Aprobar
            </v-btn>
            <v-btn dark small rounded outlined color="white" class="mr-2" @click="rejectFromCompare">
              <v-icon left small>mdi-close-circle</v-icon> Rechazar
            </v-btn>
          </template>
        </v-toolbar>

        <!-- Loading -->
        <v-container v-if="compareLoading" fluid class="pa-8">
          <v-row justify="center">
            <v-col cols="5"><v-skeleton-loader type="card, table"></v-skeleton-loader></v-col>
            <v-col cols="1" class="d-flex align-center justify-center"><v-icon size="40" color="grey lighten-2">mdi-swap-horizontal</v-icon></v-col>
            <v-col cols="5"><v-skeleton-loader type="card, table"></v-skeleton-loader></v-col>
          </v-row>
        </v-container>

        <!-- Content -->
        <v-container v-else fluid class="compare-container pa-4" style="height: calc(100vh - 48px); overflow-y: auto;">
          <!-- Validation banner: eNCF / RNC Emisor / RNC Receptor / Monto -->
          <v-row v-if="compareSystemInvoice && compareDgiiParsed" justify="center" class="mb-4">
            <v-col cols="12" md="10">
              <v-alert
                :type="allFieldsMatch ? 'success' : (matchScore >= 2 ? 'warning' : 'error')"
                text dense border="left" class="rounded-lg mb-0"
              >
                <div class="d-flex align-center justify-space-between flex-wrap">
                  <div class="d-flex align-center flex-wrap">
                    <span class="font-weight-bold mr-3">
                      <v-icon left small>{{ allFieldsMatch ? 'mdi-check-decagram' : 'mdi-alert-circle' }}</v-icon>
                      Validación {{ matchScore }}/4
                      <span v-if="invoiceDirection" class="text-caption ml-1">
                        ({{ invoiceDirection === 'emitida' ? 'venta emitida' : 'compra recibida' }})
                      </span>
                    </span>
                    <v-chip x-small label dark :color="ncfMatch ? 'success' : 'error'" class="mr-2 my-1 font-weight-bold">
                      <v-icon left x-small>{{ ncfMatch ? 'mdi-check' : 'mdi-close' }}</v-icon>
                      eNCF
                    </v-chip>
                    <v-chip x-small label dark :color="rncEmisorMatch ? 'success' : 'error'" class="mr-2 my-1 font-weight-bold">
                      <v-icon left x-small>{{ rncEmisorMatch ? 'mdi-check' : 'mdi-close' }}</v-icon>
                      RNC Emisor
                    </v-chip>
                    <v-chip x-small label dark :color="rncReceptorMatch ? 'success' : 'error'" class="mr-2 my-1 font-weight-bold">
                      <v-icon left x-small>{{ rncReceptorMatch ? 'mdi-check' : 'mdi-close' }}</v-icon>
                      RNC Receptor
                    </v-chip>
                    <v-chip x-small label dark :color="montoMatch ? 'success' : 'error'" class="mr-2 my-1 font-weight-bold">
                      <v-icon left x-small>{{ montoMatch ? 'mdi-check' : 'mdi-close' }}</v-icon>
                      Monto
                    </v-chip>
                  </div>
                </div>
                <div class="text-caption mt-1" v-if="montoMatchLabel.sysLabel">
                  {{ montoMatchLabel.sysLabel }} &nbsp;|&nbsp; {{ montoMatchLabel.dgiiLabel }}
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <v-row justify="center">
            <!-- ===== COLUMNA IZQUIERDA: FACTURA DEL SISTEMA ===== -->
            <v-col cols="12" md="5">
              <v-card class="rounded-lg overflow-hidden" elevation="3" height="100%">
                <v-toolbar flat color="blue darken-1" dark dense>
                  <v-icon left small>mdi-desktop-mac</v-icon>
                  <v-toolbar-title class="text-body-1 font-weight-bold">Factura del Sistema</v-toolbar-title>
                </v-toolbar>

                <v-card-text v-if="!compareSystemInvoice" class="text-center py-12">
                  <v-icon size="64" color="grey lighten-2">mdi-file-search-outline</v-icon>
                  <div class="text-body-1 grey--text mt-3">No se encontró factura en el sistema con NCF</div>
                  <div class="text-h6 font-weight-bold primary--text mt-1">{{ compareEncf }}</div>
                </v-card-text>

                <v-card-text v-else class="pa-5">
                  <!-- Header -->
                  <div class="d-flex align-center mb-4">
                    <v-avatar color="blue lighten-4" size="40" class="mr-3">
                      <v-icon color="blue darken-1">mdi-receipt</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-overline grey--text mb-n1">{{ compareSystemInvoice.invoice_type_name }}</div>
                      <div class="text-h6 font-weight-black primary--text">{{ compareSystemInvoice.invoice_ncf || compareSystemInvoice.supplier_invoice_voucher || compareSystemInvoice.invoice_number }}</div>
                      <div v-if="!compareSystemInvoice.invoice_ncf && compareSystemInvoice.supplier_invoice_voucher" class="text-caption grey--text">
                        eNCF Suplidor
                      </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip small label :color="compareSystemInvoice.status_id == 1 ? 'success lighten-5' : 'error lighten-5'" :text-color="compareSystemInvoice.status_id == 1 ? 'success' : 'error'" class="font-weight-bold">
                      {{ compareSystemInvoice.status_id == 1 ? 'ACTIVA' : 'ANULADA' }}
                    </v-chip>
                  </div>

                  <v-divider class="mb-4"></v-divider>

                  <!-- Info cards -->
                  <v-row dense>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Unidad de Negocio</div>
                        <div class="text-body-2 font-weight-bold">{{ compareSystemInvoice.business_unit_description }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Localidad</div>
                        <div class="text-body-2 font-weight-bold">{{ compareSystemInvoice.location_description || '-' }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Proveedor / Cliente</div>
                        <div class="text-body-2 font-weight-bold">{{ compareSystemInvoice.entity_name }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">RNC / Cédula Entidad</div>
                        <div class="text-body-2 font-weight-bold d-flex align-center">
                          {{ compareSystemInvoice.entity_document_id }}
                          <v-icon v-if="rncEmisorMatch || rncReceptorMatch" right x-small color="success">mdi-check-circle</v-icon>
                          <v-icon v-else-if="compareDgiiParsed" right x-small color="error">mdi-alert-circle</v-icon>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">RNC Unidad de Negocio</div>
                        <div class="text-body-2 font-weight-bold">{{ compareSystemInvoice.business_unit_rnc }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Fecha Emisión</div>
                        <div class="text-body-2 font-weight-bold">{{ formatDate(compareSystemInvoice.emission_date) }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">No. Factura</div>
                        <div class="text-body-2 font-weight-bold">{{ compareSystemInvoice.invoice_number }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" v-if="compareSystemInvoice.supplier_invoice_number">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">No. Factura Suplidor</div>
                        <div class="text-body-2 font-weight-bold">{{ compareSystemInvoice.supplier_invoice_number }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" v-if="compareSystemInvoice.supplier_invoice_voucher">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">eNCF Suplidor</div>
                        <div class="text-body-2 font-weight-bold d-flex align-center">
                          {{ compareSystemInvoice.supplier_invoice_voucher }}
                          <v-icon v-if="ncfMatch" right x-small color="success">mdi-check-circle</v-icon>
                          <v-icon v-else-if="compareDgiiParsed" right x-small color="error">mdi-alert-circle</v-icon>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Contabilizada</div>
                        <v-chip x-small label :color="compareSystemInvoice.recorded ? 'success lighten-5' : 'warning lighten-5'" :text-color="compareSystemInvoice.recorded ? 'success' : 'warning'" class="font-weight-bold">
                          {{ compareSystemInvoice.recorded ? 'SÍ' : 'NO' }}
                        </v-chip>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Detalle items -->
                  <div class="text-overline primary--text mt-4 mb-2">DETALLE DE FACTURA</div>
                  <v-simple-table dense class="compare-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th class="text-right">Cant.</th>
                        <th class="text-right">Precio</th>
                        <th class="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(d, i) in compareSystemDetails" :key="i">
                        <td class="text-caption">{{ d.item_description || d.item_name }}</td>
                        <td class="text-right text-caption">{{ d.quantity }}</td>
                        <td class="text-right text-caption">{{ formatCurrency(d.price) }}</td>
                        <td class="text-right text-caption font-weight-bold">{{ formatCurrency(d.total_amount) }}</td>
                      </tr>
                      <tr v-if="!compareSystemDetails.length">
                        <td colspan="4" class="text-center text-caption grey--text py-3">Sin detalles</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Totales -->
                  <v-card flat class="mt-4 pa-3 rounded-lg grey lighten-5">
                    <div class="d-flex justify-space-between mb-1">
                      <span class="text-body-2 grey--text">Subtotal</span>
                      <span class="text-body-2 font-weight-bold">{{ formatCurrency(compareSystemInvoice.subtotal_amount) }}</span>
                    </div>
                    <div class="d-flex justify-space-between mb-1" v-if="parseFloat(compareSystemInvoice.discount_amount) > 0">
                      <span class="text-body-2 grey--text">Descuento</span>
                      <span class="text-body-2 font-weight-bold error--text">-{{ formatCurrency(compareSystemInvoice.discount_amount) }}</span>
                    </div>
                    <div class="d-flex justify-space-between mb-2">
                      <span class="text-body-2 grey--text">ITBIS</span>
                      <span class="text-body-2 font-weight-bold">{{ formatCurrency(compareSystemInvoice.taxes_amount) }}</span>
                    </div>
                    <v-divider class="mb-2"></v-divider>
                    <div class="d-flex justify-space-between">
                      <span class="text-subtitle-1 font-weight-black">TOTAL</span>
                      <span class="text-subtitle-1 font-weight-black primary--text">{{ compareSystemInvoice.currency_code }} {{ formatCurrency(compareSystemInvoice.total_amount) }}</span>
                    </div>
                  </v-card>

                  <!-- Elaborado por -->
                  <div class="mt-4 text-caption grey--text">
                    <v-icon x-small class="mr-1">mdi-account</v-icon>
                    Registrado por <strong>{{ compareSystemInvoice.user_fullname }}</strong> el {{ formatDate(compareSystemInvoice.created_date) }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- ===== SEPARADOR CENTRAL ===== -->
            <v-col cols="12" md="1" class="d-flex align-center justify-center">
              <div class="text-center">
                <v-icon size="36" color="grey lighten-1">mdi-swap-horizontal-bold</v-icon>
                <div class="text-overline grey--text mt-1">VS</div>
              </div>
            </v-col>

            <!-- ===== COLUMNA DERECHA: FACTURA DGII RECIBIDA ===== -->
            <v-col cols="12" md="5">
              <v-card class="rounded-lg overflow-hidden" elevation="3" height="100%">
                <v-toolbar flat color="orange darken-2" dark dense>
                  <v-icon left small>mdi-cloud-download</v-icon>
                  <v-toolbar-title class="text-body-1 font-weight-bold">Documento DGII Recibido</v-toolbar-title>
                </v-toolbar>

                <v-card-text v-if="!compareDgiiInvoice" class="text-center py-12">
                  <v-icon size="64" color="grey lighten-2">mdi-cloud-off-outline</v-icon>
                  <div class="text-body-1 grey--text mt-3">No se encontró documento DGII recibido con e-NCF</div>
                  <div class="text-h6 font-weight-bold orange--text text--darken-2 mt-1">{{ compareEncf }}</div>
                </v-card-text>

                <v-card-text v-else class="pa-5">
                  <!-- Header -->
                  <div class="d-flex align-center mb-4">
                    <v-avatar color="orange lighten-4" size="40" class="mr-3">
                      <v-icon color="orange darken-2">mdi-receipt</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-overline grey--text mb-n1">{{ getTipoCFDesc(compareDgiiParsed.eNCF) }}</div>
                      <div class="text-h6 font-weight-black orange--text text--darken-2 d-flex align-center">
                        {{ compareDgiiParsed.eNCF || compareDgiiInvoice.Encf }}
                        <v-icon v-if="compareSystemInvoice && ncfMatch" right small color="success">mdi-check-decagram</v-icon>
                        <v-icon v-else-if="compareSystemInvoice" right small color="error">mdi-alert-circle</v-icon>
                      </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip small label color="blue lighten-5" text-color="blue" class="font-weight-bold">
                      {{ compareDgiiInvoice.Estado }}
                    </v-chip>
                  </div>

                  <v-divider class="mb-4"></v-divider>

                  <!-- Info cards -->
                  <v-row dense>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Emisor</div>
                        <div class="text-body-2 font-weight-bold">{{ compareDgiiParsed.Emisor || '-' }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">RNC Emisor</div>
                        <div class="text-body-2 font-weight-bold d-flex align-center">
                          {{ compareDgiiParsed.RncEmisor || compareDgiiInvoice.RncEmisor }}
                          <v-icon v-if="compareSystemInvoice && rncEmisorMatch" right x-small color="success">mdi-check-circle</v-icon>
                          <v-icon v-else-if="compareSystemInvoice" right x-small color="error">mdi-alert-circle</v-icon>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Receptor</div>
                        <div class="text-body-2 font-weight-bold">{{ compareDgiiParsed.Receptor || '-' }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">RNC Receptor</div>
                        <div class="text-body-2 font-weight-bold d-flex align-center">
                          {{ compareDgiiParsed.RncReceptor || compareDgiiInvoice.RncReceptor }}
                          <v-icon v-if="compareSystemInvoice && rncReceptorMatch" right x-small color="success">mdi-check-circle</v-icon>
                          <v-icon v-else-if="compareSystemInvoice" right x-small color="error">mdi-alert-circle</v-icon>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Fecha Emisión</div>
                        <div class="text-body-2 font-weight-bold">{{ formatDate(compareDgiiParsed.FechaEmision || compareDgiiInvoice.FechaEmision) }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Fecha Recepción</div>
                        <div class="text-body-2 font-weight-bold">{{ formatDate(compareDgiiInvoice.FechaRecepcion) }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6" v-if="compareDgiiParsed.NumeroFacturaInterna">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">No. Factura Interna</div>
                        <div class="text-body-2 font-weight-bold">{{ compareDgiiParsed.NumeroFacturaInterna }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="compare-field">
                        <div class="text-overline grey--text mb-0">Ambiente</div>
                        <v-chip x-small label color="blue lighten-5" text-color="blue" class="font-weight-bold">
                          {{ compareDgiiInvoice.Ambiente }}
                        </v-chip>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Detalle items DGII -->
                  <div class="text-overline orange--text text--darken-2 mt-4 mb-2">DETALLE DE FACTURA</div>
                  <v-simple-table dense class="compare-table">
                    <thead>
                      <tr>
                        <th>Descripción</th>
                        <th class="text-right">Cant.</th>
                        <th class="text-right">Precio</th>
                        <th class="text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(d, i) in compareDgiiParsed.DetallesItems" :key="i">
                        <td class="text-caption">{{ d.descripcion || d.nombre }}</td>
                        <td class="text-right text-caption">{{ d.cantidad }}</td>
                        <td class="text-right text-caption">{{ formatCurrency(d.precioUnitario) }}</td>
                        <td class="text-right text-caption font-weight-bold">{{ formatCurrency(d.monto) }}</td>
                      </tr>
                      <tr v-if="!compareDgiiParsed.DetallesItems || !compareDgiiParsed.DetallesItems.length">
                        <td colspan="4" class="text-center text-caption grey--text py-3">Sin detalles en XML</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Totales -->
                  <v-card flat class="mt-4 pa-3 rounded-lg orange lighten-5">
                    <div class="d-flex justify-space-between mb-1" v-if="compareDgiiParsed.MontoExento">
                      <span class="text-body-2 grey--text text--darken-1">Monto Exento</span>
                      <span class="text-body-2 font-weight-bold">{{ formatCurrency(compareDgiiParsed.MontoExento) }}</span>
                    </div>
                    <div class="d-flex justify-space-between mb-1" v-if="compareDgiiParsed.MontoImpuesto">
                      <span class="text-body-2 grey--text text--darken-1">Impuestos</span>
                      <span class="text-body-2 font-weight-bold">{{ formatCurrency(compareDgiiParsed.MontoImpuesto) }}</span>
                    </div>
                    <v-divider class="mb-2" v-if="compareDgiiParsed.MontoExento || compareDgiiParsed.MontoImpuesto"></v-divider>
                    <div class="d-flex justify-space-between">
                      <span class="text-subtitle-1 font-weight-black">TOTAL (DOP)</span>
                      <span class="text-subtitle-1 font-weight-black orange--text text--darken-3">RD$ {{ formatCurrency(compareDgiiParsed.MontoTotal || compareDgiiInvoice.MontoTotal) }}</span>
                    </div>
                    <!-- Otra Moneda -->
                    <template v-if="compareDgiiParsed.OtraMoneda">
                      <v-divider class="my-2"></v-divider>
                      <div class="d-flex justify-space-between mb-1">
                        <span class="text-body-2 grey--text text--darken-1">
                          <v-icon x-small class="mr-1">mdi-currency-usd</v-icon>
                          Tasa de cambio
                        </span>
                        <span class="text-body-2 font-weight-bold">1 {{ compareDgiiParsed.OtraMoneda.TipoMoneda }} = {{ compareDgiiParsed.OtraMoneda.TipoCambio }} DOP</span>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span class="text-subtitle-1 font-weight-black">TOTAL ({{ compareDgiiParsed.OtraMoneda.TipoMoneda }})</span>
                        <span class="text-subtitle-1 font-weight-black blue--text text--darken-2">{{ compareDgiiParsed.OtraMoneda.TipoMoneda }} {{ formatCurrency(compareDgiiParsed.OtraMoneda.MontoTotalOtraMoneda) }}</span>
                      </div>
                    </template>
                  </v-card>

                  <!-- Firma -->
                  <div class="mt-4 text-caption grey--text" v-if="compareDgiiParsed.FechaHoraFirma">
                    <v-icon x-small class="mr-1">mdi-certificate</v-icon>
                    Firmado: {{ compareDgiiParsed.FechaHoraFirma }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right rounded="lg">
      <div class="d-flex align-center">
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
      </div>
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  name: "CommercialApprovals",
  data: () => ({
    loading: false,
    submitting: false,
    activeTab: 0,
    lastFilters: {},
    currentRnc: "",

    // Pendientes (Facturas recibidas sin aprobación)
    pendingInvoices: [],
    selectedPending: [],
    searchPending: "",
    pagePending: 1,
    pageCountPending: 0,

    // Enviadas
    approvalsSent: [],
    searchSent: "",
    pageSent: 1,
    pageCountSent: 0,

    // Recibidas
    approvalsReceived: [],
    searchReceived: "",
    pageReceived: 1,
    pageCountReceived: 0,

    // Dialog detalle
    dialogDetail: false,
    selectedItem: null,
    detailType: "",

    // Dialog aprobación
    dialogApprove: false,
    approveItem: null,
    approveBulkItems: [],

    // Dialog rechazo
    dialogReject: false,
    rejectItems: [],
    rejectForm: {
      codigoMotivo: "",
      detalle: "",
    },

    // Comparación fullscreen
    dialogCompare: false,
    compareLoading: false,
    compareEncf: "",
    compareOriginalItem: null,
    compareSystemInvoice: null,
    compareSystemDetails: [],
    compareDgiiInvoice: null,
    compareDgiiParsed: null,

    // Snackbar
    snackbar: { show: false, text: "", color: "success", icon: "mdi-check" },

    // Motivos de rechazo DGII
    rejectionReasons: [
      { value: "01", text: "01 - Compra no realizada" },
      { value: "02", text: "02 - Error en el e-NCF" },
      { value: "03", text: "03 - Error en monto" },
      { value: "04", text: "04 - Compra de bienes o prestación de servicios no realizada" },
      { value: "05", text: "05 - Otros" },
    ],

    // Headers Pendientes
    headersPending: [
      { text: "e-NCF", value: "Encf"},
      { text: "Emisor", value: "emisorName", width: "auto" },
      { text: "RNC Emisor", value: "RncEmisor", width: "9%" },
      { text: "Monto Total", value: "MontoTotal", align: "end", width: "9%" },
      { text: "Fecha Emisión", value: "FechaEmision", width: "9%" },
      { text: "Fecha Recepción", value: "FechaRecepcion", width: "9%" },
      { text: "Estado", value: "Estado", align: "center", width: "7%" },
      { text: "Sistema", value: "existeEnSistema", align: "center", sortable: true, width: "7%" },
      { text: "Acciones", value: "actions", sortable: false, align: "center", width: "10%" },
    ],

    // Headers Enviadas
    headersSent: [
      { text: "e-NCF", value: "EncfRelacionado", width: "13%" },
      { text: "Tipo Doc.", value: "TipoDocumento", align: "center", width: "7%" },
      { text: "RNC Emisor", value: "RncEmisor", width: "9%" },
      { text: "RNC Receptor", value: "RncReceptor", width: "9%" },
      { text: "Estado", value: "Estado", align: "center", width: "9%" },
      { text: "Intentos", value: "IntentosEnvio", align: "center", width: "7%" },
      { text: "Fecha Procesamiento", value: "FechaProcesamiento", width: "11%" },
      { text: "Fecha Envío", value: "FechaEnvio", width: "11%" },
      { text: "Track ID", value: "TrackId", width: "10%" },
      { text: "", value: "actions", sortable: false, width: "4%" },
    ],

    // Headers Recibidas
    headersReceived: [
      { text: "e-NCF", value: "EncfRelacionado", width: "12%" },
      { text: "Tipo e-CF", value: "TipoeCF", align: "center", width: "7%" },
      { text: "Emisor", value: "NombreEmisor", width: "14%" },
      { text: "RNC Emisor", value: "RncEmisor", width: "9%" },
      { text: "Estado", value: "Estado", align: "center", width: "9%" },
      { text: "Monto Total", value: "MontoTotal", align: "end", width: "10%" },
      { text: "ITBIS", value: "MontoITBIS", align: "end", width: "8%" },
      { text: "Fecha Recepción", value: "FechaRecepcion", width: "11%" },
      { text: "Fecha Aprobación", value: "FechaAprobacionComercial", width: "11%" },
      { text: "", value: "actions", sortable: false, width: "4%" },
    ],
  }),

  computed: {
    hasData() {
      return this.pendingInvoices.length > 0 || this.approvalsSent.length > 0 || this.approvalsReceived.length > 0;
    },
    approveBulkTotal() {
      return this.approveBulkItems.reduce((sum, inv) => sum + (parseFloat(inv.MontoTotal) || 0), 0);
    },
    errorCount() {
      const erroresEnv = this.approvalsSent.filter(
        (i) => i.Estado && (i.Estado.toUpperCase() === "ERROR" || i.Estado.toUpperCase() === "RECHAZADO")
      ).length;
      const erroresRec = this.approvalsReceived.filter(
        (i) => i.Estado && i.Estado.toUpperCase() === "RECHAZADO"
      ).length;
      return erroresEnv + erroresRec;
    },
    detailFields() {
      if (!this.selectedItem) return [];
      const item = this.selectedItem;
      const fields = [];

      if (this.detailType === "enviada") {
        fields.push(
          { label: "e-NCF Relacionado", value: item.EncfRelacionado },
          { label: "Tipo Documento", value: item.TipoDocumento },
          { label: "RNC Emisor", value: item.RncEmisor },
          { label: "RNC Receptor", value: item.RncReceptor },
          { label: "Estado", value: item.Estado, isStatus: true },
          { label: "Intentos de Envío", value: item.IntentosEnvio },
          { label: "Track ID", value: item.TrackId },
          { label: "Ambiente", value: item.Ambiente },
          { label: "Código Seguridad", value: item.CodigoSeguridad },
          { label: "Fecha/Hora Firma", value: item.FechaHoraFirma },
          { label: "Fecha Procesamiento", value: item.FechaProcesamiento, isDate: true },
          { label: "Fecha Envío", value: item.FechaEnvio, isDate: true },
          { label: "Fecha Creación", value: item.FechaCreacion, isDate: true }
        );
      } else if (this.detailType === "recibida") {
        fields.push(
          { label: "e-NCF Relacionado", value: item.EncfRelacionado },
          { label: "Tipo e-CF", value: item.TipoeCF },
          { label: "Emisor", value: item.NombreEmisor },
          { label: "Comprador", value: item.NombreComprador },
          { label: "RNC Emisor", value: item.RncEmisor },
          { label: "RNC Receptor", value: item.RncReceptor },
          { label: "Estado", value: item.Estado, isStatus: true },
          { label: "Monto Total", value: item.MontoTotal, isCurrency: true },
          { label: "Monto ITBIS", value: item.MontoITBIS, isCurrency: true },
          { label: "Ambiente", value: item.Ambiente },
          { label: "ID Documento", value: item.IdDocumento },
          { label: "Secuencia Interna", value: item.SecuenciaInterna },
          { label: "Fecha/Hora Firma", value: item.FechaHoraFirma },
          { label: "Fecha Recepción", value: item.FechaRecepcion, isDate: true },
          { label: "Fecha Aprobación", value: item.FechaAprobacionComercial, isDate: true },
          { label: "Hora Aprobación", value: item.HoraAprobacionComercial },
          { label: "Versión", value: item.Version },
          { label: "Fecha Creación", value: item.FechaCreacion, isDate: true }
        );
      }
      return fields;
    },
    montoMatch() {
      if (!this.compareSystemInvoice || !this.compareDgiiParsed) return false;
      const sysTotal = parseFloat(this.compareSystemInvoice.total_amount) || 0;
      const sysCurrency = (this.compareSystemInvoice.currency_code || "").toUpperCase();
      let dgiiTotal;
      if (sysCurrency !== "DOP" && this.compareDgiiParsed.OtraMoneda && this.compareDgiiParsed.OtraMoneda.TipoMoneda?.toUpperCase() === sysCurrency) {
        dgiiTotal = parseFloat(this.compareDgiiParsed.OtraMoneda.MontoTotalOtraMoneda) || 0;
      } else {
        dgiiTotal = parseFloat(this.compareDgiiParsed.MontoTotal || this.compareDgiiInvoice?.MontoTotal) || 0;
      }
      return Math.abs(sysTotal - dgiiTotal) < 0.02;
    },
    montoMatchLabel() {
      if (!this.compareSystemInvoice || !this.compareDgiiParsed) return {};
      const sysTotal = parseFloat(this.compareSystemInvoice.total_amount) || 0;
      const sysCurrency = (this.compareSystemInvoice.currency_code || "").toUpperCase();
      let dgiiTotal, dgiiLabel;
      if (sysCurrency !== "DOP" && this.compareDgiiParsed.OtraMoneda && this.compareDgiiParsed.OtraMoneda.TipoMoneda?.toUpperCase() === sysCurrency) {
        dgiiTotal = parseFloat(this.compareDgiiParsed.OtraMoneda.MontoTotalOtraMoneda) || 0;
        dgiiLabel = `DGII (${sysCurrency}): ${this.formatCurrency(dgiiTotal)}`;
      } else {
        dgiiTotal = parseFloat(this.compareDgiiParsed.MontoTotal || this.compareDgiiInvoice?.MontoTotal) || 0;
        dgiiLabel = `DGII (DOP): ${this.formatCurrency(dgiiTotal)}`;
      }
      return { sysLabel: `Sistema (${sysCurrency}): ${this.formatCurrency(sysTotal)}`, dgiiLabel };
    },

    // ---- Match eNCF: DGII.eNCF vs sistema (invoice_ncf en ventas, supplier_invoice_voucher en compras) ----
    ncfMatch() {
      if (!this.compareSystemInvoice || !this.compareDgiiParsed) return false;
      const dgiiNcf = (this.compareDgiiParsed.eNCF || this.compareDgiiInvoice?.Encf || "").toString().trim().toUpperCase();
      if (!dgiiNcf) return false;
      const sysNcf = (this.compareSystemInvoice.invoice_ncf || "").toString().trim().toUpperCase();
      const sysSupplierNcf = (this.compareSystemInvoice.supplier_invoice_voucher || "").toString().trim().toUpperCase();
      return dgiiNcf === sysNcf || dgiiNcf === sysSupplierNcf;
    },

    // ---- Match RNC Emisor: en compras = entity_document_id (proveedor); en ventas = business_unit_rnc (nosotros) ----
    rncEmisorMatch() {
      if (!this.compareSystemInvoice || !this.compareDgiiParsed) return false;
      const dgiiEmisor = (this.compareDgiiParsed.RncEmisor || this.compareDgiiInvoice?.RncEmisor || "").toString().trim();
      if (!dgiiEmisor) return false;
      const sysEntityRnc = (this.compareSystemInvoice.entity_document_id || "").toString().trim();
      const sysOwnRnc = (this.compareSystemInvoice.business_unit_rnc || "").toString().trim();
      return dgiiEmisor === sysEntityRnc || dgiiEmisor === sysOwnRnc;
    },

    // ---- Match RNC Receptor: en compras = business_unit_rnc (nosotros); en ventas = entity_document_id (cliente) ----
    rncReceptorMatch() {
      if (!this.compareSystemInvoice || !this.compareDgiiParsed) return false;
      const dgiiReceptor = (this.compareDgiiParsed.RncReceptor || this.compareDgiiInvoice?.RncReceptor || "").toString().trim();
      if (!dgiiReceptor) return false;
      const sysEntityRnc = (this.compareSystemInvoice.entity_document_id || "").toString().trim();
      const sysOwnRnc = (this.compareSystemInvoice.business_unit_rnc || "").toString().trim();
      return dgiiReceptor === sysOwnRnc || dgiiReceptor === sysEntityRnc;
    },

    // ---- Detección de dirección: ¿esta factura la EMITIMOS o la RECIBIMOS? ----
    invoiceDirection() {
      if (!this.compareDgiiParsed || !this.currentRnc) return null;
      const dgiiEmisor = (this.compareDgiiParsed.RncEmisor || this.compareDgiiInvoice?.RncEmisor || "").toString().trim();
      const dgiiReceptor = (this.compareDgiiParsed.RncReceptor || this.compareDgiiInvoice?.RncReceptor || "").toString().trim();
      const myRnc = this.currentRnc.toString().trim();
      if (dgiiEmisor === myRnc) return "emitida"; // venta
      if (dgiiReceptor === myRnc) return "recibida"; // compra
      return null;
    },

    // ---- Estado global del match ----
    allFieldsMatch() {
      return this.montoMatch && this.ncfMatch && this.rncEmisorMatch && this.rncReceptorMatch;
    },

    // ---- Cantidad de validaciones que pasan (para mostrar X/4) ----
    matchScore() {
      let score = 0;
      if (this.ncfMatch) score++;
      if (this.rncEmisorMatch) score++;
      if (this.rncReceptorMatch) score++;
      if (this.montoMatch) score++;
      return score;
    },
  },

  methods: {
    changeBusiness(data) {
      if (data.business == 0) {
        this.getData({
          business: this.$store.state.auth.user.acess_business_unit_finance,
          dateStart: data.dateStart,
          dateEnd: data.dateEnd,
        });
      } else {
        this.getData(data);
      }
    },

    async getData(data) {
      const businessUnit = this.$store.state.auth.bussines_units.find(
        (element) => element.value === data.business
      );

      if (!businessUnit || !businessUnit.rnc) {
        this.pendingInvoices = [];
        this.approvalsSent = [];
        this.approvalsReceived = [];
        return;
      }

      this.currentRnc = businessUnit.rnc;
      this.lastFilters = data;
      this.loading = true;
      this.selectedPending = [];

      const payload = {
        rnc: businessUnit.rnc,
        dateStart: data.dateStart,
        dateEnd: data.dateEnd,
      };

      try {
        const [resPending, resSent, resReceived] = await Promise.all([
          this.api_Post("accounting/commercial_approvals_pending", payload),
          this.api_Post("accounting/commercial_approvals_sent", payload),
          this.api_Post("accounting/commercial_approvals_received", payload),
        ]);

        const pendingData = resPending.ok ? await resPending.json() : [];
        pendingData.forEach((inv) => {
          const nameMatch = /<RazonSocialEmisor>([^<]+)<\/RazonSocialEmisor>/.exec(inv.ContenidoXml);
          inv.emisorName = nameMatch ? nameMatch[1] : '';
        });
        this.pendingInvoices = pendingData;
        this.approvalsSent = resSent.ok ? await resSent.json() : [];
        this.approvalsReceived = resReceived.ok ? await resReceived.json() : [];
      } catch (error) {
        console.error("Error fetching commercial approvals:", error);
        this.pendingInvoices = [];
        this.approvalsSent = [];
        this.approvalsReceived = [];
      } finally {
        this.loading = false;
      }
    },

    // ---- Abrir dialog de aprobación individual ----
    openApproveDialog(item) {
      this.approveItem = item;
      this.approveBulkItems = [item];
      this.dialogApprove = true;
    },

    // ---- Abrir dialog de aprobación en lote ----
    openBulkApproveDialog() {
      if (this.selectedPending.length === 0) return;
      this.approveBulkItems = [...this.selectedPending];
      this.approveItem = this.approveBulkItems[0];
      this.dialogApprove = true;
    },

    // ---- Confirmar aprobación desde el dialog ----
    async confirmApproveFromDialog() {
      this.submitting = true;
      try {
        const promises = this.approveBulkItems.map((item) =>
          this.submitApproval(item, "APROBADO")
        );
        await Promise.all(promises);
        this.showSnackbar(
          this.approveBulkItems.length === 1
            ? "Factura aprobada correctamente"
            : `${this.approveBulkItems.length} factura(s) aprobada(s) correctamente`,
          "success",
          "mdi-check-circle"
        );
        this.dialogApprove = false;
        this.selectedPending = [];
        this.refreshData();
      } catch (e) {
        this.showSnackbar("Error al aprobar: " + e.message, "error", "mdi-alert-circle");
      } finally {
        this.submitting = false;
      }
    },

    // ---- Pasar del dialog de aprobación al de rechazo ----
    openRejectFromApproveDialog() {
      this.rejectItems = [...this.approveBulkItems];
      this.rejectForm = { codigoMotivo: "", detalle: "" };
      this.$nextTick(() => {
        this.dialogReject = true;
      });
    },

    // ---- Aprobar desde dialog de comparación ----
    approveFromCompare() {
      if (!this.compareOriginalItem) return;
      this.dialogCompare = false;
      this.$nextTick(() => {
        this.openApproveDialog(this.compareOriginalItem);
      });
    },

    // ---- Rechazar desde dialog de comparación ----
    rejectFromCompare() {
      if (!this.compareOriginalItem) return;
      this.dialogCompare = false;
      this.$nextTick(() => {
        this.rejectItems = [this.compareOriginalItem];
        this.rejectForm = { codigoMotivo: "", detalle: "" };
        this.dialogReject = true;
      });
    },

    // ---- Abrir dialog rechazo individual ----
    openRejectOne(item) {
      this.rejectItems = [item];
      this.rejectForm = { codigoMotivo: "", detalle: "" };
      this.dialogReject = true;
    },

    // ---- Abrir dialog rechazo en lote ----
    openBulkReject() {
      this.rejectItems = [...this.selectedPending];
      this.rejectForm = { codigoMotivo: "", detalle: "" };
      this.dialogReject = true;
    },

    // ---- Confirmar rechazo ----
    async confirmReject() {
      this.submitting = true;
      try {
        const promises = this.rejectItems.map((item) =>
          this.submitApproval(item, "RECHAZADO", this.rejectForm.codigoMotivo, this.rejectForm.detalle)
        );
        await Promise.all(promises);
        this.showSnackbar(
          `${this.rejectItems.length} factura(s) rechazada(s)`,
          "warning",
          "mdi-close-circle"
        );
        this.dialogReject = false;
        this.selectedPending = [];
        this.refreshData();
      } catch (e) {
        this.showSnackbar("Error al rechazar: " + e.message, "error", "mdi-alert-circle");
      } finally {
        this.submitting = false;
      }
    },

    // ---- Enviar aprobación/rechazo al backend ----
    // En aprobaciones comerciales sobre facturas pendientes, NOSOTROS somos siempre el receptor.
    // El emisor es el suplidor que nos envió el e-CF; el receptor es nuestro currentRnc.
    async submitApproval(item, estado, codigoMotivoRechazo, motivoRechazo) {
      const encf = item.Encf || item.EncfRelacionado;
      if (!encf) {
        throw new Error("Item sin eNCF: no se puede enviar la aprobación");
      }
      const tipoDoc = encf.substring(1, 3);

      const rncEmisor = (item.RncEmisor || "").toString().trim();
      const rncReceptor = this.currentRnc; // somos el receptor en este flujo

      if (!rncEmisor) {
        throw new Error("Item sin RncEmisor: el e-CF no tiene proveedor identificado");
      }
      if (!rncReceptor) {
        throw new Error("Unidad de negocio sin RNC: no se puede enviar la aprobación");
      }
      // Sanity check: si el item trae RncReceptor y no coincide con currentRnc, avisar
      if (item.RncReceptor && item.RncReceptor.toString().trim() !== rncReceptor) {
        console.warn(
          `submitApproval: item.RncReceptor (${item.RncReceptor}) != currentRnc (${rncReceptor}). Usando currentRnc.`
        );
      }

      const res = await this.api_Post("accounting/commercial_approvals_submit", {
        rncEmisor,
        rncReceptor,
        encf,
        tipoDocumento: tipoDoc,
        ambiente: item.Ambiente || "ecf",
        estado,
        codigoMotivoRechazo: codigoMotivoRechazo || null,
        motivoRechazo: motivoRechazo || null,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || "Error desconocido al enviar aprobación");
      }
      return res.json();
    },

    // ---- Refrescar datos ----
    refreshData() {
      if (this.lastFilters && this.lastFilters.business) {
        this.getData(this.lastFilters);
      }
    },

    showDetail(item, type) {
      this.selectedItem = { ...item };
      this.detailType = type;
      this.dialogDetail = true;
    },

    showSnackbar(text, color, icon) {
      this.snackbar = { show: true, text, color, icon };
    },

    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY HH:mm") : "";
    },

    formatCurrency(value) {
      return value != null
        ? new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(value)
        : "";
    },

    getStatusColor(status) {
      if (!status) return "grey lighten-3";
      const s = status.toUpperCase();
      if (s === "PROCESADO" || s === "ACEPTADO" || s === "APROBADO") return "green lighten-5";
      if (s === "PENDIENTE" || s === "SIN_ACCION") return "orange lighten-5";
      if (s === "ERROR" || s === "RECHAZADO") return "red lighten-5";
      if (s === "ENVIADO") return "blue lighten-5";
      return "grey lighten-4";
    },

    getStatusTextColor(status) {
      if (!status) return "grey";
      const s = status.toUpperCase();
      if (s === "PROCESADO" || s === "ACEPTADO" || s === "APROBADO") return "green darken-2";
      if (s === "PENDIENTE" || s === "SIN_ACCION") return "orange darken-2";
      if (s === "ERROR" || s === "RECHAZADO") return "red darken-2";
      if (s === "ENVIADO") return "blue darken-2";
      return "grey darken-1";
    },

    // ---- Abrir dialog de comparación ----
    async openCompareDialog(item) {
      const encf = item.Encf || item.EncfRelacionado;
      if (!encf) return;

      this.compareEncf = encf;
      this.compareOriginalItem = item;
      this.compareSystemInvoice = null;
      this.compareSystemDetails = [];
      this.compareDgiiInvoice = null;
      this.compareDgiiParsed = null;
      this.compareLoading = true;
      this.dialogCompare = true;

      try {
        // search_invoice_by_ncf (backend) debe matchear así:
        //   bu.rnc                = :rnc         (nuestra unidad de negocio)
        //   e.document_id         = :rncEmisor   (proveedor en compras, cliente en ventas)
        //   ( i.invoice_ncf       = :ncf
        //     OR i.supplier_invoice_voucher = :ncf )   ← critical: cover both ventas e-NCF y compras eNCF del proveedor
        //   AND i.status_id = 1
        //
        // search_dgii_received_by_encf busca contra dgii."FacturasElectronicasRecibidas":
        //   WHERE "Encf" = :encf AND "RncReceptor" = :rnc
        const [resSys, resDgii] = await Promise.all([
          this.api_Post("accounting/search_invoice_by_ncf", {
            ncf: encf,
            rnc: this.currentRnc,
            rncEmisor: item.RncEmisor,
          }),
          this.api_Post("accounting/search_dgii_received_by_encf", {
            encf: encf,
            rnc: this.currentRnc,
          }),
        ]);

        if (resSys.ok) {
          const sysData = await resSys.json();
          this.compareSystemInvoice = sysData.header || null;
          this.compareSystemDetails = sysData.details || [];
        }

        if (resDgii.ok) {
          const dgiiData = await resDgii.json();
          this.compareDgiiInvoice = dgiiData;
          if (dgiiData && dgiiData.ContenidoXml) {
            this.compareDgiiParsed = this.parseDgiiXml(dgiiData.ContenidoXml);
          } else {
            this.compareDgiiParsed = dgiiData ? { MontoTotal: dgiiData.MontoTotal } : null;
          }
        }
      } catch (error) {
        console.error("Error loading comparison data:", error);
      } finally {
        this.compareLoading = false;
      }
    },

    parseDgiiXml(xmlString) {
      try {
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlString || "", "text/xml");
        const h = xml.querySelector("Encabezado") || xml;
        const idDoc = h.querySelector("IdDoc") || h;
        const emisor = h.querySelector("Emisor") || h;
        const comprador = h.querySelector("Comprador") || h;
        const t = h.querySelector("Totales") || xml;

        const parsed = {
          eNCF: idDoc.querySelector("eNCF")?.textContent,
          FechaEmision: emisor.querySelector("FechaEmision")?.textContent,
          RncEmisor: emisor.querySelector("RNCEmisor")?.textContent,
          Emisor: emisor.querySelector("RazonSocialEmisor")?.textContent,
          NombreComercialEmisor: emisor.querySelector("NombreComercial")?.textContent,
          NumeroFacturaInterna: emisor.querySelector("NumeroFacturaInterna")?.textContent,
          RncReceptor: comprador.querySelector("RNCComprador")?.textContent,
          Receptor: comprador.querySelector("RazonSocialComprador")?.textContent,
          MontoExento: t.querySelector("MontoExento")?.textContent,
          MontoImpuesto: t.querySelector("MontoImpuestoAdicional")?.textContent,
          MontoTotal: t.querySelector("MontoTotal")?.textContent,
          FechaHoraFirma: xml.querySelector("FechaHoraFirma")?.textContent,
          OtraMoneda: null,
          DetallesItems: [],
        };

        const otraMoneda = h.querySelector("OtraMoneda");
        if (otraMoneda) {
          parsed.OtraMoneda = {
            TipoMoneda: otraMoneda.querySelector("TipoMoneda")?.textContent,
            TipoCambio: otraMoneda.querySelector("TipoCambio")?.textContent,
            MontoExentoOtraMoneda: otraMoneda.querySelector("MontoExentoOtraMoneda")?.textContent,
            MontoTotalOtraMoneda: otraMoneda.querySelector("MontoTotalOtraMoneda")?.textContent,
          };
        }

        const items = Array.from(xml.querySelectorAll("DetallesItems > Item") || []);
        if (items.length > 0) {
          parsed.DetallesItems = items.map((item) => {
            let codigo = "";
            const codigosItem = item.querySelector("TablaCodigosItem > CodigosItem");
            if (codigosItem) {
              codigo = codigosItem.querySelector("CodigoItem")?.textContent || "";
            } else {
              codigo = item.querySelector("CodigoItem")?.textContent || "";
            }
            return {
              codigo,
              nombre: item.querySelector("NombreItem")?.textContent,
              descripcion: item.querySelector("DescripcionItem")?.textContent,
              cantidad: item.querySelector("CantidadItem")?.textContent,
              precioUnitario: item.querySelector("PrecioUnitarioItem")?.textContent,
              monto: item.querySelector("MontoItem")?.textContent,
            };
          });
        }

        return parsed;
      } catch (error) {
        console.error("Error parsing DGII XML:", error);
        return { DetallesItems: [] };
      }
    },

    getTipoCFDesc(code) {
      if (typeof code === "string" && code.startsWith("E")) {
        code = code.substring(1, 3);
      }
      const tipos = {
        "31": "Factura de Crédito Fiscal Electrónica",
        "32": "Factura de Consumo Electrónica",
        "33": "Nota de Débito Electrónica",
        "34": "Nota de Crédito Electrónica",
        "41": "Compras Electrónico",
        "43": "Gastos Menores Electrónico",
        "44": "Regímenes Especiales Electrónico",
        "45": "Gubernamental Electrónico",
        "46": "Comprobante de Exportaciones Electrónico",
        "47": "Comprobante para Pagos al Exterior Electrónico",
      };
      return tipos[code] || code || "";
    },
  },
};
</script>

<style scoped>
.modern-table >>> .v-data-table__wrapper {
  scrollbar-width: thin;
}
.modern-table >>> thead th {
  background-color: #f5f5f5 !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  letter-spacing: 0.5px;
}
.modern-table >>> tbody tr:hover {
  background-color: #e3f2fd !important;
}

/* Comparison dialog styles */
.compare-container {
  background: #f5f7fa;
}
.compare-field {
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}
.compare-table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
}
.compare-table thead tr {
  background-color: #f8f9fa;
}
.compare-table thead th {
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.compare-table tbody td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.cursor-pointer {
  cursor: pointer;
}
.hover-underline:hover {
  text-decoration: underline;
}
</style>
